import React, { useState, useEffect, useCallback } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Button } from '@material-ui/core';

import TextField from '../components/TextField';
import FullScreenDialog from '../components/FullScreenDialog';
import DatePicker from '../components/DatePicker';
import DateTimePicker from '../components/DateTimePicker';
import Select from '../components/Select';
import Radio from '../components/Radio';

import useQueryParams from '../hooks/useQueryParams';
import useSnackbarText from '../hooks/useSnackbarText';
import useDiaryData from '../hooks/useDiaryData';
import useMainContext from '../hooks/useMainContext';
import usePlanList from '../hooks/usePlanList';

import { 
	submissionStatusOptions,
	submissionModeOptions,
	submissionIsExistingClientOptions,
	submissionHasNomineeOptions,
	submissionCarAssistantOptions,
	submissionMethodOptions, 
} from '../configs/submissions';
import birthdates from '../api/birthdates';
import submissions from '../api/submissions';
import { maxWidth } from '../components/configs';

const queryParamKey = 'create-submission';

const generateDefaultState = (selectedDate) => {
	return {
		submissionDate: selectedDate,
		lifeAssured: '',
		policyNumber: '',
		birthdate: null,
		contact: '',
		planID: '',
		BSA: '',
		status: submissionStatusOptions[0].value,
		issuedDate: null,
		mode: submissionModeOptions[0].value,
		initial: '',
		isExistingClient: submissionIsExistingClientOptions[0].value,
		hasNominee: submissionHasNomineeOptions[0].value,
		carAssistant: submissionCarAssistantOptions[0].value,
		method: submissionMethodOptions[0].value,
	}
};

const useStyles = makeStyles(theme => ({
	content: {
		maxWidth,
		margin: 'auto',
		width: '100%',
		backgroundColor: '#fff',
		padding: 10,
		alignItems: 'center',
		display: 'flex',
		flexDirection: 'column'
	},
	submitButton: {
		width: '100%',
		marginTop: 20,
		marginBottom: 20,
		color: '#fff',
		borderRadius: 6,
		fontWeight: 600,
		backgroundColor: theme.palette.success.main,
		'&:hover': {
			backgroundColor: theme.palette.success.dark,
		}
	}
}));

function CreateSubmissionDialog(props) {
	const classes = useStyles();
	const planList = usePlanList();
	// eslint-disable-next-line
	const [diary, _, handleSetDate, handleManualFetch] = useDiaryData();
	const handleSnackbarChange = useSnackbarText();
	const [queryParams, setQueryParams] = useQueryParams();
	const [state, _setState] = useState(generateDefaultState(diary.selectedDate));
	const [error, setError] = useState({});
	const [disabled, setDisable] = useState(false);
	const { onSubmissionClose } = useMainContext();

	const queryValue = queryParams[queryParamKey];

	const setState = useCallback((newState = {}) => {
		_setState((prevState) => ({
			...prevState,
			...newState,
		}));
	}, []);

	const handleValueCheck = () => {
		let noError = true;
		let error = {};
		if(!state.lifeAssured) {
			noError = false;
			error.lifeAssured = 'Invalid value.';
		}
		// if(!state.policyNumber) {
		// 	noError = false;
		// 	error.policyNumber = 'Invalid value.';
		// }
		if(!state.birthdate) {
			noError = false;
			error.birthdate = 'Invalid value.';
		}
		if(!state.contact) {
			noError = false;
			error.contact = 'Invalid value.';
		}
		if(!state.planID) {
			noError = false;
			error.planID = 'Invalid value.';
		}
		if(!state.BSA) {
			noError = false;
			error.BSA = 'Invalid value.';
		}
		if(!state.initial) {
			noError = false;
			error.initial = 'Invalid value.';
		}
		setError(error);
		return noError;
	};

	const handleChange = (type) => (event, value) => {
		if(value !== undefined) setState({ [type]: value });
		else setState({ [type]: event.target.value });
	};

	const handleSubmit = async () => {
		if(!handleValueCheck()) {
			handleSnackbarChange({ text: 'Invalid inputs', variant: 'error' });
			return window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
		};

		setDisable(true);
		let { data, success } = await submissions.createSubmission(state);
		setDisable(false);

		// create birthdate
		await birthdates.createBirthdate({
			submissionID: data.submissionID,
			name: state.lifeAssured,
			date: new Date(state.birthdate),
			contact: state.contact,
		});

		if(success) {
			handleSnackbarChange({ text: 'New submission created', variant: 'success' });
		} else {
			return handleSnackbarChange({ text: data.message, variant: 'error' });
		}

		if(onSubmissionClose) onSubmissionClose({ ...state, submissionID: data.submissionID });
		setQueryParams({ 'create-submission': null });
	};

	// set default plan 
	useEffect(() => {
		if(planList.length && !state.planID) {
			setState({ planID: planList[0].options[0].value });
		}
	}, [planList, state.planID, setState]);

	useEffect(() => {
		_setState(generateDefaultState(diary.selectedDate));
	}, [queryValue, diary.selectedDate]);

	return (
		<FullScreenDialog 
			title="Create New Submission"
			queryParamKey={queryParamKey}
		>
			<div className={classes.content}>
				<DatePicker
					label="Submission Date"
					value={state.submissionDate}
					onChange={handleChange('submissionDate')}
				/>

				<TextField 
					label="Life Assured*"
					value={state.lifeAssured}
					onChange={handleChange('lifeAssured')}
					error={Boolean(error.lifeAssured)}
					helperText={error.lifeAssured}
				/>

				<DateTimePicker
					label="Birthdate*"
					value={state.birthdate}
					onChange={handleChange('birthdate')}
					error={Boolean(error.birthdate)}
					helperText={error.birthdate}
				/>

				<TextField 
					label="Contact*"
					value={state.contact}
					onChange={handleChange('contact')}
					error={Boolean(error.contact)}
					helperText={error.contact}
				/>

				<TextField 
					label="Policy Number"
					value={state.policyNumber}
					onChange={handleChange('policyNumber')}
					error={Boolean(error.policyNumber)}
					helperText={error.policyNumber}
				/>

				<Select 
					label="Plan*"
					options={planList}
					value={state.planID}
					onChange={handleChange('planID')}
					error={Boolean(error.planID)}
					helperText={error.planID}
					group
				/>

				<TextField 
					label="BSA*"
					value={state.BSA}
					onChange={handleChange('BSA')}
					type="number"
					error={Boolean(error.BSA)}
					helperText={error.BSA}
				/>

				<Select 
					label="Mode"
					options={submissionModeOptions}
					value={state.mode}
					onChange={handleChange('mode')}
				/>

				<TextField 
					label="Initial*"
					value={state.initial}
					onChange={handleChange('initial')}
					type="number"
					error={Boolean(error.initial)}
					helperText={error.initial}
				/>

				<Radio 
					label="Client"
					options={submissionIsExistingClientOptions}
					value={state.isExistingClient}
					onChange={handleChange('isExistingClient')}
					row
				/>

				<Radio 
					label="Nominee"
					options={submissionHasNomineeOptions}
					value={state.hasNominee}
					onChange={handleChange('hasNominee')}
					row
				/>

				<Radio 
					label="Car Assistant"
					options={submissionCarAssistantOptions}
					value={state.carAssistant}
					onChange={handleChange('carAssistant')}
				/>

				<Select 
					label="Method"
					options={submissionMethodOptions}
					value={state.method}
					onChange={handleChange('method')}
				/>

				<Button
					className={classes.submitButton}
					disabled={disabled}
					variant="contained"
					color="primary"
					onClick={handleSubmit}
				>
					Create
				</Button>
			</div>
		</FullScreenDialog>
	);
}

export default CreateSubmissionDialog;
